import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const EVENT_STORAGE_KEY = 'clEventCode';
const REFERRAL_STORAGE_KEY = 'clReferralCode';
const DEFAULT_PROMO_CODE = '';

// Get the API URL from environment variables
const API_URL = import.meta.env.VITE_WORKER_URL || '';

export interface PromoData {
  code: string;
  valid: boolean;
  type?: 'event' | 'referral';
  expires_at?: number;
  description?: string;
  applies_to?: { lookup_keys?: string[] };
}

interface UsePromoCodeResult {
  eventCode: string;
  /** API result for the event code (valid only; invalid event code is cleared from state). */
  eventData?: PromoData;
  referralCode: string;
  /** API result for the referral code. */
  referralData?: PromoData;
  isLoading: boolean;
  error: Error | null;
  setEventCode: (code: string) => void;
  setReferralCode: (code: string, prefetchedData?: PromoData) => void;
  clearReferralCode: () => void;
  /** Call API to validate a code; returns promo data. View handles result (set event/referral, show error). */
  validateReferralCode: (code: string) => Promise<PromoData>;
  /** True if event code is valid for display (not expired, applies to lookup key). */
  eventValidForPlan: (lookupKey: string) => boolean;
  /** True if referral code is valid for display (not expired, applies to lookup key). */
  referralValidForPlan: (lookupKey: string) => boolean;
  /** Event first, then referral; only returns one that is valid for plan. Use for countdown. */
  getCountdownPromo: (lookupKey: string) => PromoData | undefined;
}

// Create context for shared promo code state
const PromoCodeContext = createContext<UsePromoCodeResult | null>(null);

async function fetchPromoInfo(promoCode: string): Promise<PromoData> {
  const response = await fetch(`${API_URL}/api/promo-info`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      promoCode,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch promo info');
  }

  return response.json();
}

/** Pick the promo (event or referral) with the nearest future expiry for countdown. */
export function getPromoWithNearestExpiry(
  eventData?: PromoData,
  referralData?: PromoData
): PromoData | undefined {
  const candidates: PromoData[] = [];
  if (eventData?.valid && eventData.expires_at) candidates.push(eventData);
  if (referralData?.valid && referralData.expires_at) candidates.push(referralData);
  if (candidates.length === 0) return undefined;
  if (candidates.length === 1) return candidates[0];
  const now = Date.now() / 1000;
  const a = candidates[0]!;
  const b = candidates[1]!;
  return (a.expires_at! - now) <= (b.expires_at! - now) ? a : b;
}

function getItem(key: string): string {
  if (typeof window === 'undefined') return '';
  try {
    const v = localStorage.getItem(key);
    return (v && v.trim()) ? v.trim() : '';
  } catch {
    return '';
  }
}

function setItem(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (value && value.trim()) {
      localStorage.setItem(key, value.trim());
    } else {
      localStorage.removeItem(key);
    }
  } catch {}
}

function getInitialCodes(): { eventCode: string; referralCode: string } {
  let eventCode = '';
  let referralCode = '';

  if (typeof window !== 'undefined') {
    try {
      const url = new URL(window.location.href);
      const urlEvent = url.searchParams.get('event');
      const urlReferral = url.searchParams.get('referral');
      if (urlEvent && urlEvent.trim()) {
        eventCode = urlEvent.trim();
        setItem(EVENT_STORAGE_KEY, eventCode);
      }
      if (urlReferral && urlReferral.trim()) {
        referralCode = urlReferral.trim();
        setItem(REFERRAL_STORAGE_KEY, referralCode);
      }
    } catch {}

    if (!eventCode) eventCode = getItem(EVENT_STORAGE_KEY);
    if (!referralCode) referralCode = getItem(REFERRAL_STORAGE_KEY);
  }

  if (!eventCode) {
    eventCode = DEFAULT_PROMO_CODE;
    setItem(EVENT_STORAGE_KEY, eventCode);
  }
  return { eventCode, referralCode };
}

// Hook to consume the promo code context
export function usePromoCode(): UsePromoCodeResult {
  const context = useContext(PromoCodeContext);

  if (!context) {
    throw new Error('usePromoCode must be used within a PromoCodeProvider');
  }

  return context;
}

// Provider component that manages the shared promo code state
export function PromoCodeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [{ eventCode, referralCode }, setCodes] = useState(getInitialCodes);
  const setEventCodeState = (v: string) => setCodes(prev => ({ ...prev, eventCode: v }));
  const setReferralCodeState = (v: string) => setCodes(prev => ({ ...prev, referralCode: v }));

  // Fetch event promo info
  const {
    data: eventPromoData,
    isLoading: isEventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ['promo-info', 'event', eventCode],
    queryFn: () => fetchPromoInfo(eventCode),
    enabled: Boolean(eventCode && eventCode.trim()),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Fetch referral promo info
  const {
    data: referralPromoData,
    isLoading: isReferralLoading,
    error: referralError,
  } = useQuery({
    queryKey: ['promo-info', 'referral', referralCode],
    queryFn: () => fetchPromoInfo(referralCode),
    enabled: Boolean(referralCode && referralCode.trim()),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Clear event code if it's invalid (no error messaging for event codes)
  useEffect(() => {
    if (eventCode && eventPromoData && !eventPromoData.valid) {
      setEventCodeState(DEFAULT_PROMO_CODE);
      setItem(EVENT_STORAGE_KEY, DEFAULT_PROMO_CODE);
    }
  }, [eventCode, eventPromoData]);


  const isLoading = isEventLoading || isReferralLoading;
  const error = (eventError as Error | null) || (referralError as Error | null) || null;

  const setEventCode = (code: string) => {
    const trimmed = code.trim();
    setEventCodeState(trimmed);
    setItem(EVENT_STORAGE_KEY, trimmed);
  };

  const setReferralCode = (code: string, prefetchedData?: PromoData) => {
    const trimmed = code.trim();
    setReferralCodeState(trimmed);
    setItem(REFERRAL_STORAGE_KEY, trimmed);
    if (prefetchedData != null) {
      queryClient.setQueryData(['promo-info', 'referral', trimmed], prefetchedData);
    }
  };

  const clearReferralCode = () => {
    setReferralCodeState('');
    setItem(REFERRAL_STORAGE_KEY, '');
  };

  const eventData = eventPromoData?.valid ? eventPromoData : undefined;
  
  const referralData = useMemo(() => {
    const data = referralPromoData ?? undefined;
    if (data?.valid && referralCode.toUpperCase() === 'WELCOME10') {
      const storageKey = 'clPromoWelcome10Claim';
      let claimTime = getItem(storageKey);
      if (!claimTime) {
        return { ...data, valid: false };
      }
      const date = new Date(parseInt(claimTime));
      
      if (import.meta.env.DEV) {
        date.setMinutes(date.getMinutes() + 5);
      } else {
        date.setHours(date.getHours() + 18);
        if (date.getMinutes() >= 30) {
          date.setHours(date.getHours() + 1);
        }
        date.setMinutes(0, 0, 0);
      }
      
      return { ...data, expires_at: Math.floor(date.getTime() / 1000) };
    }
    return data;
  }, [referralPromoData, referralCode]);

  useEffect(() => {
    if (referralCode.toUpperCase() === 'WELCOME10' && referralData?.valid && referralData?.expires_at) {
      const msUntilExpiry = (referralData.expires_at * 1000) - Date.now();
      if (msUntilExpiry <= 0) {
        clearReferralCode();
        localStorage.removeItem('clPromoWelcome10Claim');
      } else {
        const timer = setTimeout(() => {
          clearReferralCode();
          localStorage.removeItem('clPromoWelcome10Claim');
        }, msUntilExpiry);
        return () => clearTimeout(timer);
      }
    }
  }, [referralCode, referralData]);

  const promoValidForPlan = useCallback((promoData: PromoData | undefined, lookupKey: string): boolean => {
    if (!promoData) return false;
    const now = Date.now() / 1000;
    if (promoData.expires_at != null && now > promoData.expires_at) return false;
    const keys = promoData.applies_to?.lookup_keys;
    if (keys && keys.length > 0) return keys.includes(lookupKey);
    return true;
  }, []);

  const eventValidForPlan = useCallback((lookupKey: string) => promoValidForPlan(eventData, lookupKey), [eventData, promoValidForPlan]);
  const referralValidForPlan = useCallback((lookupKey: string) => promoValidForPlan(referralData, lookupKey), [referralData, promoValidForPlan]);

  const getCountdownPromo = useCallback((lookupKey: string): PromoData | undefined => {
    const eventValid = eventValidForPlan(lookupKey) && eventData?.expires_at;
    const referralValid = referralValidForPlan(lookupKey) && referralData?.expires_at;

    if (eventValid && referralValid) {
      return getPromoWithNearestExpiry(eventData, referralData);
    }

    if (eventValid) return eventData;
    if (referralValid) return referralData;
    return undefined;
  }, [eventData, referralData, eventValidForPlan, referralValidForPlan]);

  const validateReferralCode = useCallback(async (code: string): Promise<PromoData> => {
    const upperValue = code.toUpperCase().trim();
    return fetchPromoInfo(upperValue);
  }, []);

  const value: UsePromoCodeResult = {
    eventCode,
    eventData,
    referralCode,
    referralData,
    isLoading,
    error: error as Error | null,
    setEventCode,
    setReferralCode,
    clearReferralCode,
    validateReferralCode,
    eventValidForPlan,
    referralValidForPlan,
    getCountdownPromo,
  };

  return (
    <PromoCodeContext.Provider value={value}>
      {children}
    </PromoCodeContext.Provider>
  );
}
